import { MOVEMENT_TUNING } from "../config/movement.ts";
import { SIMULATION_TIMING } from "../config/simulation.ts";
import {
  clamp,
  normalizePlanar,
  wrapRadians,
  type Vector3,
} from "../core/math.ts";
import type { PlayerId } from "../core/ids.ts";
import {
  INPUT_INTENT_SCHEMA_VERSION,
  type InputIntent,
} from "../input/intents.ts";
import type {
  CharacterMotionRequest,
  CharacterMotionResolution,
} from "../physics/contracts.ts";
import type { SimulationEvent } from "./events.ts";
import type {
  GameState,
  PlayerLocomotionMode,
  PlayerState,
} from "./state.ts";

export interface PlannedPlayerTick {
  playerId: PlayerId;
  lookYaw: number;
  facingYaw: number;
  isSprinting: boolean;
  isFlying: boolean;
  hasMovementInput: boolean;
  jumpStarted: boolean;
  coyoteTicksRemaining: number;
  jumpBufferTicksRemaining: number;
  desiredVelocity: Vector3;
  motion: CharacterMotionRequest;
}

export interface GameTickPlan {
  tick: number;
  deltaSeconds: number;
  playersById: Record<PlayerId, PlannedPlayerTick>;
  motionRequests: readonly CharacterMotionRequest[];
}

export interface GameTickResult {
  state: GameState;
  events: readonly SimulationEvent[];
  motionRequests: readonly CharacterMotionRequest[];
}

function movePlanarVelocityToward(
  velocityX: number,
  velocityZ: number,
  targetX: number,
  targetZ: number,
  maximumDelta: number,
): Readonly<{ x: number; z: number }> {
  const deltaX = targetX - velocityX;
  const deltaZ = targetZ - velocityZ;
  const deltaLength = Math.hypot(deltaX, deltaZ);

  if (deltaLength <= maximumDelta || deltaLength <= Number.EPSILON) {
    return { x: targetX, z: targetZ };
  }

  const scale = maximumDelta / deltaLength;
  return {
    x: velocityX + deltaX * scale,
    z: velocityZ + deltaZ * scale,
  };
}

function moveValueToward(
  current: number,
  target: number,
  maximumDelta: number,
): number {
  const delta = target - current;
  if (Math.abs(delta) <= maximumDelta) {
    return target;
  }
  return current + Math.sign(delta) * maximumDelta;
}

function limitPlanarMagnitude(
  x: number,
  z: number,
  maximum: number,
): Readonly<{ x: number; z: number }> {
  const length = Math.hypot(x, z);
  if (length <= maximum || length <= Number.EPSILON) {
    return { x, z };
  }
  const scale = maximum / length;
  return { x: x * scale, z: z * scale };
}

function durationTicks(seconds: number, deltaSeconds: number): number {
  return Math.max(1, Math.ceil(seconds / deltaSeconds - 1e-9));
}

function moveAngleToward(
  currentRadians: number,
  targetRadians: number,
  maximumDeltaRadians: number,
): number {
  const delta = wrapRadians(targetRadians - currentRadians);
  if (Math.abs(delta) <= maximumDeltaRadians) {
    return wrapRadians(targetRadians);
  }
  return wrapRadians(
    currentRadians + Math.sign(delta) * maximumDeltaRadians,
  );
}

function intentsForTick(
  intents: readonly Readonly<InputIntent>[],
  tick: number,
): ReadonlyMap<PlayerId, Readonly<InputIntent>> {
  const byPlayerId = new Map<PlayerId, Readonly<InputIntent>>();
  for (const intent of intents) {
    if (
      intent.tick === tick &&
      intent.schemaVersion === INPUT_INTENT_SCHEMA_VERSION
    ) {
      byPlayerId.set(intent.playerId, intent);
    }
  }
  return byPlayerId;
}

function planPlayerTick(
  player: Readonly<PlayerState>,
  intent: Readonly<InputIntent> | undefined,
  tick: number,
  deltaSeconds: number,
): PlannedPlayerTick {
  const lookYaw = wrapRadians(intent?.lookYaw ?? player.lookYaw);
  const input = normalizePlanar(
    clamp(intent?.moveX ?? 0, -1, 1),
    clamp(intent?.moveZ ?? 0, -1, 1),
  );
  const hasMovementInput =
    Math.abs(input.x) > Number.EPSILON ||
    Math.abs(input.z) > Number.EPSILON;
  const wasFlying = player.locomotion.mode === "flying";
  const isFlying = intent?.toggleFlightPressed === true
    ? !wasFlying
    : wasFlying;
  const exitedFlight = wasFlying && !isFlying;
  const verticalFlightInput =
    Number(intent?.ascendHeld === true) -
    Number(intent?.descendHeld === true);
  const flightInputLength = Math.hypot(
    input.x,
    input.z,
    verticalFlightInput,
  );
  const flightInputScale = flightInputLength > 1
    ? 1 / flightInputLength
    : 1;
  const hasFlightInput = flightInputLength > Number.EPSILON;

  // Local +Z means camera-forward. Because the camera looks along world +Z
  // at yaw zero, its visible screen-right is world -X in Three's right-handed
  // coordinates.
  const sinYaw = Math.sin(lookYaw);
  const cosYaw = Math.cos(lookYaw);
  const directionX = -input.x * cosYaw + input.z * sinYaw;
  const directionZ = input.x * sinYaw + input.z * cosYaw;
  const isSprinting =
    (isFlying ? hasFlightInput : hasMovementInput) &&
    (intent?.sprintHeld ?? false);
  const targetSpeed = isFlying
    ? isSprinting
      ? MOVEMENT_TUNING.boostedFlightSpeedMetresPerSecond
      : MOVEMENT_TUNING.flightSpeedMetresPerSecond
    : isSprinting
      ? MOVEMENT_TUNING.sprintSpeedMetresPerSecond
      : MOVEMENT_TUNING.walkSpeedMetresPerSecond;
  const targetVelocityX =
    hasMovementInput
      ? directionX * targetSpeed * (isFlying ? flightInputScale : 1)
      : 0;
  const targetVelocityZ =
    hasMovementInput
      ? directionZ * targetSpeed * (isFlying ? flightInputScale : 1)
      : 0;
  const acceleration = isFlying
    ? hasFlightInput
      ? MOVEMENT_TUNING.flightAccelerationMetresPerSecondSquared
      : MOVEMENT_TUNING.flightDecelerationMetresPerSecondSquared
    : hasMovementInput
      ? MOVEMENT_TUNING.accelerationMetresPerSecondSquared
      : MOVEMENT_TUNING.decelerationMetresPerSecondSquared;
  const startingPlanarVelocity = exitedFlight
    ? limitPlanarMagnitude(
        player.velocity.x,
        player.velocity.z,
        MOVEMENT_TUNING.sprintSpeedMetresPerSecond,
      )
    : player.velocity;
  const planarVelocity = movePlanarVelocityToward(
    startingPlanarVelocity.x,
    startingPlanarVelocity.z,
    targetVelocityX,
    targetVelocityZ,
    acceleration * deltaSeconds,
  );

  const coyoteWindowTicks = durationTicks(
    MOVEMENT_TUNING.coyoteTimeSeconds,
    deltaSeconds,
  );
  let coyoteTicksRemaining = !isFlying && player.locomotion.grounded
    ? coyoteWindowTicks
    : isFlying
      ? 0
      : Math.max(0, player.locomotion.coyoteTicksRemaining - 1);
  const jumpBufferWindowTicks = durationTicks(
    MOVEMENT_TUNING.jumpBufferTimeSeconds,
    deltaSeconds,
  );
  let jumpBufferTicksRemaining = !isFlying && intent?.jumpPressed === true
    ? jumpBufferWindowTicks
    : isFlying
      ? 0
      : Math.max(0, player.locomotion.jumpBufferTicksRemaining - 1);
  const jumpStarted =
    jumpBufferTicksRemaining > 0 &&
    (player.locomotion.grounded || coyoteTicksRemaining > 0);
  if (jumpStarted) {
    jumpBufferTicksRemaining = 0;
    coyoteTicksRemaining = 0;
  }

  const regularVerticalVelocity = jumpStarted
    ? MOVEMENT_TUNING.jumpLaunchSpeedMetresPerSecond
    : player.locomotion.grounded
      ? 0
      : (exitedFlight ? 0 : player.velocity.y) -
        MOVEMENT_TUNING.gravityMetresPerSecondSquared * deltaSeconds;
  const flightVerticalVelocity = moveValueToward(
    player.velocity.y,
    verticalFlightInput * targetSpeed * flightInputScale,
    (hasFlightInput
      ? MOVEMENT_TUNING.flightAccelerationMetresPerSecondSquared
      : MOVEMENT_TUNING.flightDecelerationMetresPerSecondSquared) *
      deltaSeconds,
  );
  const desiredVelocity = {
    x: planarVelocity.x,
    y: isFlying ? flightVerticalVelocity : regularVerticalVelocity,
    z: planarVelocity.z,
  };
  const targetFacingYaw = Math.atan2(directionX, directionZ);
  const facingYaw = hasMovementInput
    ? moveAngleToward(
        player.facingYaw,
        targetFacingYaw,
        MOVEMENT_TUNING.turnSpeedRadiansPerSecond * deltaSeconds,
      )
    : player.facingYaw;

  return {
    playerId: player.id,
    lookYaw,
    facingYaw,
    isSprinting,
    isFlying,
    hasMovementInput,
    jumpStarted,
    coyoteTicksRemaining,
    jumpBufferTicksRemaining,
    desiredVelocity,
    motion: {
      playerId: player.id,
      tick,
      deltaSeconds,
      currentPosition: { ...player.position },
      currentVelocity: { ...player.velocity },
      desiredMovement: {
        x: desiredVelocity.x * deltaSeconds,
        y: desiredVelocity.y * deltaSeconds,
        z: desiredVelocity.z * deltaSeconds,
      },
      groundedLastTick: isFlying ? false : player.locomotion.grounded,
      jumpRequested: jumpStarted,
      mantleRequested:
        !isFlying &&
        hasMovementInput &&
        intent?.jumpPressed === true,
      forward: hasMovementInput
        ? { x: directionX, z: directionZ }
        : { x: sinYaw, z: cosYaw },
      capsule: {
        radiusM: MOVEMENT_TUNING.characterCapsuleRadiusMetres,
        halfHeightM:
          MOVEMENT_TUNING.characterCapsuleHeightMetres / 2 -
          MOVEMENT_TUNING.characterCapsuleRadiusMetres,
      },
      maxStepHeightM: isFlying
        ? 0
        : MOVEMENT_TUNING.maximumStepHeightMetres,
      maxSlopeAngleRadians:
        MOVEMENT_TUNING.maximumWalkableSlopeRadians,
      maxMantleHeightM: MOVEMENT_TUNING.mantleMaximumHeightMetres,
    },
  };
}

export function planGameTick(
  state: Readonly<GameState>,
  intents: readonly Readonly<InputIntent>[],
  deltaSeconds = SIMULATION_TIMING.fixedStepSeconds,
): GameTickPlan {
  if (!Number.isFinite(deltaSeconds) || deltaSeconds <= 0) {
    throw new RangeError("deltaSeconds must be a finite, positive number");
  }

  const tick = state.tick + 1;
  const tickIntents = intentsForTick(intents, tick);
  const playersById: Record<PlayerId, PlannedPlayerTick> = {};
  const motionRequests: CharacterMotionRequest[] = [];
  const playerEntries = Object.entries(state.playersById).sort(
    ([left], [right]) => left.localeCompare(right),
  );

  for (const [playerId, player] of playerEntries) {
    const planned = planPlayerTick(
      player,
      tickIntents.get(playerId),
      tick,
      deltaSeconds,
    );
    playersById[playerId] = planned;
    motionRequests.push(planned.motion);
  }

  return {
    tick,
    deltaSeconds,
    playersById,
    motionRequests,
  };
}

function fallbackMotionResolution(
  player: Readonly<PlayerState>,
  request: Readonly<CharacterMotionRequest>,
): CharacterMotionResolution {
  const proposedPosition = {
    x: request.currentPosition.x + request.desiredMovement.x,
    y: request.currentPosition.y + request.desiredMovement.y,
    z: request.currentPosition.z + request.desiredMovement.z,
  };
  const remainsGrounded =
    request.groundedLastTick &&
    !request.jumpRequested;
  const reachesFallbackGround =
    !remainsGrounded && proposedPosition.y <= 0;
  const grounded = remainsGrounded || reachesFallbackGround;
  const position = grounded
    ? {
        x: proposedPosition.x,
        y: remainsGrounded ? player.position.y : 0,
        z: proposedPosition.z,
      }
    : proposedPosition;
  const velocity = grounded
    ? {
        x: request.desiredMovement.x / request.deltaSeconds,
        y: 0,
        z: request.desiredMovement.z / request.deltaSeconds,
      }
    : {
        x: request.desiredMovement.x / request.deltaSeconds,
        y: request.desiredMovement.y / request.deltaSeconds,
        z: request.desiredMovement.z / request.deltaSeconds,
      };

  return {
    playerId: request.playerId,
    tick: request.tick,
    resolvedPosition: position,
    resolvedVelocity: velocity,
    grounded,
    groundNormal: grounded ? { x: 0, y: 1, z: 0 } : null,
    groundSlopeAngleRadians: grounded ? 0 : null,
    surfaceId: null,
    stepped: false,
    mantle: {
      possible: false,
      targetPosition: null,
      heightM: null,
      obstructionId: null,
      landingSurface: null,
    },
    contacts: [],
  };
}

function resolutionMap(
  plan: Readonly<GameTickPlan>,
  resolutions: readonly Readonly<CharacterMotionResolution>[],
): ReadonlyMap<PlayerId, Readonly<CharacterMotionResolution>> {
  const byPlayerId = new Map<
    PlayerId,
    Readonly<CharacterMotionResolution>
  >();
  for (const resolution of resolutions) {
    if (resolution.tick !== plan.tick) {
      throw new Error(
        `Character motion resolution tick ${resolution.tick} does not match ${plan.tick}`,
      );
    }
    if (plan.playersById[resolution.playerId] === undefined) {
      throw new Error(
        `Character motion resolution references unknown player ${resolution.playerId}`,
      );
    }
    if (byPlayerId.has(resolution.playerId)) {
      throw new Error(
        `Duplicate motion resolution for player ${resolution.playerId}`,
      );
    }
    if (resolution.grounded && resolution.groundNormal === null) {
      throw new Error(
        `Grounded resolution for ${resolution.playerId} needs ground data`,
      );
    }
    byPlayerId.set(resolution.playerId, resolution);
  }
  return byPlayerId;
}

function locomotionModeFor(
  resolution: Readonly<CharacterMotionResolution>,
  mantleStarted: boolean,
  isFlying: boolean,
): PlayerLocomotionMode {
  if (isFlying) {
    return "flying";
  }
  if (mantleStarted) {
    return "mantling";
  }
  if (resolution.grounded) {
    return "grounded";
  }
  return resolution.resolvedVelocity.y > 0 ? "jumping" : "falling";
}

function landingImpactSpeed(
  planned: Readonly<PlannedPlayerTick>,
): number {
  return Math.max(
    0,
    -planned.desiredVelocity.y,
  );
}

function completePlayerTick(
  player: Readonly<PlayerState>,
  planned: Readonly<PlannedPlayerTick>,
  resolution: Readonly<CharacterMotionResolution>,
  tick: number,
  deltaSeconds: number,
): Readonly<{ player: PlayerState; events: readonly SimulationEvent[] }> {
  const events: SimulationEvent[] = [];
  const validMantleTarget = resolution.mantle.possible
    ? resolution.mantle.targetPosition
    : null;
  const mantleStarted =
    planned.motion.mantleRequested && validMantleTarget !== null;
  const mode = locomotionModeFor(
    resolution,
    mantleStarted,
    planned.isFlying,
  );
  const grounded = !planned.isFlying && resolution.grounded;
  const landed =
    (player.locomotion.mode === "jumping" ||
      player.locomotion.mode === "falling") &&
    grounded;
  const landingCount = player.locomotion.landingCount + Number(landed);
  const landingResponseTicks = durationTicks(
    MOVEMENT_TUNING.landingResponseSeconds,
    deltaSeconds,
  );
  const landingResponseTicksRemaining = landed
    ? landingResponseTicks
    : Math.max(0, player.locomotion.landingResponseTicksRemaining - 1);
  const surfaceId = planned.isFlying ? null : resolution.surfaceId;

  if (landed) {
    events.push({
      id: `landing:${player.id}:${landingCount}`,
      type: "landing",
      tick,
      playerId: player.id,
      sequence: landingCount,
      surfaceId,
      impactSpeedMetresPerSecond: landingImpactSpeed(planned),
      position: { ...resolution.resolvedPosition },
    });
  }

  const horizontalDistance = Math.hypot(
    resolution.resolvedPosition.x - player.position.x,
    resolution.resolvedPosition.z - player.position.z,
  );
  const planarSpeed = Math.hypot(
    resolution.resolvedVelocity.x,
    resolution.resolvedVelocity.z,
  );
  const canEmitSteps =
    mode === "grounded" &&
    grounded &&
    surfaceId !== null &&
    planarSpeed >= MOVEMENT_TUNING.minimumFootstepSpeedMetresPerSecond;
  let footstepDistanceMetres = canEmitSteps
    ? player.locomotion.footstepDistanceMetres + horizontalDistance
    : 0;
  let footstepSequence = player.locomotion.footstepSequence;
  const footstepCadence = planned.isSprinting
    ? MOVEMENT_TUNING.sprintFootstepDistanceMetres
    : MOVEMENT_TUNING.walkFootstepDistanceMetres;

  while (canEmitSteps && footstepDistanceMetres >= footstepCadence) {
    footstepDistanceMetres -= footstepCadence;
    footstepSequence += 1;
    events.push({
      id: `surface-step:${player.id}:${footstepSequence}`,
      type: "surface-step",
      tick,
      playerId: player.id,
      sequence: footstepSequence,
      foot: footstepSequence % 2 === 1 ? "left" : "right",
      surfaceId,
      position: { ...resolution.resolvedPosition },
    });
  }

  const coyoteWindowTicks = durationTicks(
    MOVEMENT_TUNING.coyoteTimeSeconds,
    deltaSeconds,
  );
  return {
    player: {
      id: player.id,
      position: { ...resolution.resolvedPosition },
      velocity: { ...resolution.resolvedVelocity },
      facingYaw: planned.facingYaw,
      lookYaw: planned.lookYaw,
      isSprinting: planned.isSprinting && (grounded || planned.isFlying),
      locomotion: {
        mode,
        grounded,
        groundNormal: !grounded || resolution.groundNormal === null
          ? { x: 0, y: 1, z: 0 }
          : { ...resolution.groundNormal },
        groundSlopeRadians: grounded
          ? resolution.groundSlopeAngleRadians ?? 0
          : 0,
        surfaceId,
        coyoteTicksRemaining: grounded
          ? coyoteWindowTicks
          : planned.coyoteTicksRemaining,
        jumpBufferTicksRemaining: planned.jumpBufferTicksRemaining,
        landingResponseTicksRemaining,
        landingCount,
        lastLandingTick: landed
          ? tick
          : player.locomotion.lastLandingTick,
        footstepDistanceMetres,
        footstepSequence,
        mantleCount:
          player.locomotion.mantleCount + Number(mantleStarted),
        mantleProgress: mantleStarted ? 1 : 0,
        lastStepHeightMetres: resolution.stepped
          ? Math.max(
              0,
              resolution.resolvedPosition.y - player.position.y,
            )
          : 0,
      },
    },
    events,
  };
}

export function completeGameTick(
  state: Readonly<GameState>,
  plan: Readonly<GameTickPlan>,
  resolutions: readonly Readonly<CharacterMotionResolution>[],
): GameTickResult {
  if (plan.tick !== state.tick + 1) {
    throw new Error(
      `Game tick plan ${plan.tick} cannot advance state tick ${state.tick}`,
    );
  }
  const byPlayerId = resolutionMap(plan, resolutions);
  const playersById: Record<PlayerId, PlayerState> = {};
  const events: SimulationEvent[] = [];
  const playerEntries = Object.entries(state.playersById).sort(
    ([left], [right]) => left.localeCompare(right),
  );

  for (const [playerId, player] of playerEntries) {
    const planned = plan.playersById[playerId];
    if (planned === undefined) {
      throw new Error(`Game tick plan is missing player ${playerId}`);
    }
    const resolution = byPlayerId.get(playerId) ??
      fallbackMotionResolution(player, planned.motion);
    const completed = completePlayerTick(
      player,
      planned,
      resolution,
      plan.tick,
      plan.deltaSeconds,
    );
    playersById[playerId] = completed.player;
    events.push(...completed.events);
  }

  return {
    state: {
      schemaVersion: state.schemaVersion,
      tick: plan.tick,
      simulationTimeSeconds:
        state.simulationTimeSeconds + plan.deltaSeconds,
      playersById,
    },
    events,
    motionRequests: plan.motionRequests,
  };
}

export function stepGameStateDetailed(
  state: Readonly<GameState>,
  intents: readonly Readonly<InputIntent>[],
  deltaSeconds = SIMULATION_TIMING.fixedStepSeconds,
  resolutions: readonly Readonly<CharacterMotionResolution>[] = [],
): GameTickResult {
  const plan = planGameTick(state, intents, deltaSeconds);
  return completeGameTick(state, plan, resolutions);
}

/**
 * Compatibility wrapper returning state only. M3 composition should call
 * `planGameTick`, resolve its motion requests through the physics adapter, then
 * call `completeGameTick` so collision outcomes remain authoritative.
 */
export function stepGameState(
  state: Readonly<GameState>,
  intents: readonly Readonly<InputIntent>[],
  deltaSeconds = SIMULATION_TIMING.fixedStepSeconds,
  resolutions: readonly Readonly<CharacterMotionResolution>[] = [],
): GameState {
  return stepGameStateDetailed(
    state,
    intents,
    deltaSeconds,
    resolutions,
  ).state;
}
